import type { Rgb } from '@/types/engine';

type ThreeModule = typeof import('three');
type OrbitControlsCtor = typeof import('three/examples/jsm/controls/OrbitControls.js').OrbitControls;

export interface ThreeStyle {
  color: Rgb;
  opacity: number;
  background: Rgb;
  pinColor: Rgb;
  showPins: boolean;
}

export class ThreeRenderer {
  private readonly THREE: ThreeModule;
  private readonly renderer: import('three').WebGLRenderer;
  private readonly scene: import('three').Scene;
  private readonly camera: import('three').PerspectiveCamera;
  private readonly controls: import('three/examples/jsm/controls/OrbitControls.js').OrbitControls;
  private readonly board: import('three').Mesh;
  private strings: import('three').LineSegments | null = null;
  private pinsMesh: import('three').InstancedMesh | null = null;
  private targetWidth = 1;
  private targetHeight = 1;
  private pins: Float32Array = new Float32Array(0);
  private sequence: Uint16Array = new Uint16Array(0);
  private upTo = 0;
  private style: ThreeStyle = { color: [20, 20, 24], opacity: 0.25, background: [250, 249, 246], pinColor: [140, 140, 140], showPins: true };
  private geometryDirty = true;
  private raf: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private disposed = false;

  private constructor(private readonly container: HTMLElement, THREE: ThreeModule, OrbitControls: OrbitControlsCtor) {
    this.THREE = THREE;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.domElement.className = 'sa-layer sa-layer-three';
    container.appendChild(this.renderer.domElement);
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(35, 1, 0.01, 100);
    this.camera.position.set(0, -1.6, 2.4);
    this.camera.up.set(0, 0, 1);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.addEventListener('change', () => this.requestFrame());

    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(1.5, -1.5, 3);
    const fill = new THREE.DirectionalLight(0xffffff, 0.35);
    fill.position.set(-2, 2, 1.5);
    this.scene.add(ambient, key, fill);

    const boardGeometry = new THREE.BoxGeometry(1, 1, 0.04);
    const boardMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, metalness: 0.0 });
    this.board = new THREE.Mesh(boardGeometry, boardMaterial);
    this.board.position.z = -0.02;
    this.scene.add(this.board);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.resize()));
      this.resizeObserver.observe(container);
    }
    this.resize();
    this.animate();
  }

  static async create(container: HTMLElement): Promise<ThreeRenderer> {
    const [THREE, { OrbitControls }] = await Promise.all([
      import('three'),
      import('three/examples/jsm/controls/OrbitControls.js'),
    ]);
    return new ThreeRenderer(container, THREE, OrbitControls);
  }

  resize(): void {
    const rect = this.container.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    this.renderer.setSize(w, h, true);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.requestFrame();
  }

  setTargetSize(width: number, height: number): void {
    this.targetWidth = Math.max(1, width);
    this.targetHeight = Math.max(1, height);
    const aspect = this.targetWidth / this.targetHeight;
    this.board.scale.set(aspect >= 1 ? 1.1 : 1.1 * aspect, aspect >= 1 ? 1.1 / aspect : 1.1, 1);
    this.geometryDirty = true;
    this.requestFrame();
  }

  setSolution(pins: Float32Array, sequence: Uint16Array): void {
    if (pins !== this.pins) this.geometryDirty = true;
    this.pins = pins;
    if (sequence.buffer !== this.sequence.buffer || sequence.length !== this.sequence.length) this.geometryDirty = true;
    this.sequence = sequence;
    this.requestFrame();
  }

  setUpTo(count: number): void {
    this.upTo = count;
    if (this.strings) this.strings.geometry.setDrawRange(0, Math.min(count, this.sequence.length >>> 1) * 2);
    this.requestFrame();
  }

  setStyle(style: ThreeStyle): void {
    this.style = style;
    const { THREE } = this;
    this.scene.background = new THREE.Color(...style.background.map((v) => v / 255) as [number, number, number]);
    (this.board.material as import('three').MeshStandardMaterial).color.setRGB(style.background[0] / 255, style.background[1] / 255, style.background[2] / 255);
    if (this.strings) {
      const m = this.strings.material as import('three').LineBasicMaterial;
      m.color.setRGB(style.color[0] / 255, style.color[1] / 255, style.color[2] / 255);
      m.opacity = style.opacity;
    }
    if (this.pinsMesh) {
      (this.pinsMesh.material as import('three').MeshStandardMaterial).color.setRGB(style.pinColor[0] / 255, style.pinColor[1] / 255, style.pinColor[2] / 255);
      this.pinsMesh.visible = style.showPins;
    }
    this.requestFrame();
  }

  private toBoard(x: number, y: number): [number, number] {
    const aspect = this.targetWidth / this.targetHeight;
    const sx = aspect >= 1 ? 1 : aspect;
    const sy = aspect >= 1 ? 1 / aspect : 1;
    return [((x / this.targetWidth) - 0.5) * sx, (0.5 - y / this.targetHeight) * sy];
  }

  private rebuildGeometry(): void {
    const { THREE } = this;
    if (this.strings) {
      this.scene.remove(this.strings);
      this.strings.geometry.dispose();
      (this.strings.material as import('three').Material).dispose();
      this.strings = null;
    }
    if (this.pinsMesh) {
      this.scene.remove(this.pinsMesh);
      this.pinsMesh.geometry.dispose();
      (this.pinsMesh.material as import('three').Material).dispose();
      this.pinsMesh = null;
    }
    const pinCount = this.pins.length >>> 1;
    const stringCount = this.sequence.length >>> 1;
    if (pinCount === 0) return;

    const pinHeight = 0.03;
    const pinGeometry = new THREE.CylinderGeometry(0.0035, 0.0035, pinHeight, 10);
    pinGeometry.rotateX(Math.PI / 2);
    const pinMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color(...this.style.pinColor.map((v) => v / 255) as [number, number, number]), metalness: 0.7, roughness: 0.35 });
    const mesh = new THREE.InstancedMesh(pinGeometry, pinMaterial, pinCount);
    const matrix = new THREE.Matrix4();
    for (let i = 0; i < pinCount; i++) {
      const [x, y] = this.toBoard(this.pins[i * 2], this.pins[i * 2 + 1]);
      matrix.makeTranslation(x, y, pinHeight / 2);
      mesh.setMatrixAt(i, matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.visible = this.style.showPins;
    this.pinsMesh = mesh;
    this.scene.add(mesh);

    if (stringCount > 0) {
      const positions = new Float32Array(stringCount * 6);
      const zStep = (pinHeight * 0.8) / Math.max(1, stringCount);
      for (let i = 0; i < stringCount; i++) {
        const a = this.sequence[i * 2];
        const b = this.sequence[i * 2 + 1];
        if (a >= pinCount || b >= pinCount) continue;
        const [x1, y1] = this.toBoard(this.pins[a * 2], this.pins[a * 2 + 1]);
        const [x2, y2] = this.toBoard(this.pins[b * 2], this.pins[b * 2 + 1]);
        const z = 0.004 + i * zStep;
        positions.set([x1, y1, z, x2, y2, z], i * 6);
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setDrawRange(0, Math.min(this.upTo, stringCount) * 2);
      const material = new THREE.LineBasicMaterial({
        color: new THREE.Color(...this.style.color.map((v) => v / 255) as [number, number, number]),
        transparent: true,
        opacity: this.style.opacity,
        depthWrite: false,
      });
      this.strings = new THREE.LineSegments(geometry, material);
      this.scene.add(this.strings);
    }
    this.geometryDirty = false;
  }

  private requestFrame(): void {
    // The animation loop renders continuously while damping; nothing to do.
  }

  private animate(): void {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(() => this.animate());
    if (this.geometryDirty) this.rebuildGeometry();
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.disposed = true;
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.resizeObserver?.disconnect();
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
